/**
 * The state behind the environment variables editor.
 *
 * Lifted out of the page because it is where the bugs were, and none of it
 * needs a browser to be wrong: rows are staged locally, compared against
 * what the server last said, and folded back together when a save or a
 * background refresh lands. That is three-way merge logic wearing a form,
 * and it earns tests of its own.
 */

import type { EnvVar } from './types'

export type EnvRow = {
  id: string
  buildTime?: boolean
  originalBuildTime?: boolean
  key: string
  value: string
  /** Whether `value` is the real one. False for a secret nobody has revealed. */
  known: boolean
  shown: boolean
  /** The key this row had on the server, or null for a row added locally. */
  originalKey: string | null
  /** The server value when known, for change detection; null when unknown. */
  originalValue: string | null
}

export function rowsFromServer(items: EnvVar[]): EnvRow[] {
  return items.map((item) => ({
    id: item.id,
    buildTime: item.build_time,
    originalBuildTime: item.build_time,
    key: item.key,
    value: item.is_secret ? '' : (item.value ?? ''),
    known: !item.is_secret,
    shown: false,
    originalKey: item.key,
    originalValue: item.is_secret ? null : (item.value ?? ''),
  }))
}

/**
 * Fold a fresh server list into the rows already on screen.
 *
 * Rebuilding the list from the server looks equivalent and is not, twice
 * over. The server returns variables sorted by key, so a row added at the
 * bottom jumped into alphabetical order the moment it saved — the list
 * rearranging itself under the cursor of somebody who has just finished
 * typing. And runtime values are stored as secrets, so the list comes back
 * with them blank: every value just entered was replaced by an empty masked
 * field, which reads as "it did not save".
 *
 * Neither is about the data. Order here is the developer's, not the
 * database's, and a value this page already has in hand does not become
 * unknown because the round trip does not return it.
 */
export function mergeFromServer(existing: EnvRow[], items: EnvVar[]): EnvRow[] {
  const byKey = new Map(items.map((item) => [item.key, item]))
  const kept: EnvRow[] = []
  for (const row of existing) {
    const item = row.originalKey === null ? undefined : byKey.get(row.originalKey)
    if (!item) continue // deleted, here or in another tab
    byKey.delete(item.key)
    kept.push({
      ...row,
      id: item.id,
      buildTime: item.build_time,
      originalBuildTime: item.build_time,
      // A readable value from the server is the truth. A secret one is not
      // returned at all, so what this page already knows stands.
      ...(item.is_secret
        ? {}
        : { value: item.value ?? '', known: true, originalValue: item.value ?? '' }),
    })
  }
  // Anything the server has that this page does not: added elsewhere, or the
  // first load. Order among these is the server's, after the known rows.
  return [...kept, ...rowsFromServer([...byKey.values()])]
}

/**
 * The rows on screen, after a save of exactly those rows succeeded.
 *
 * They *are* the saved state, so they become the new baseline in place. The
 * page used to discard them and wait for a refetch, which did two bad things
 * at once: the section flashed a loading skeleton where somebody's variables
 * had been, and — because "no rows" reads as "every variable was deleted" —
 * the editor latched into a dirty state that suppressed the very refresh it
 * was waiting for. The skeleton then stayed until the page was reloaded.
 */
export function rebaseAfterSave(rows: EnvRow[]): EnvRow[] {
  return rows
    .filter((row) => row.key !== '')
    .map((row) => ({
      ...row,
      originalKey: row.key,
      originalBuildTime: row.buildTime,
      originalValue: row.known ? row.value : row.originalValue,
    }))
}

export type ChangeSet = {
  /** Server keys no row claims any more: deleted here. */
  deletedKeys: string[]
  /** Rows that are new, renamed, re-scoped or re-valued. */
  changedRows: EnvRow[]
  dirty: boolean
}

/**
 * What is staged, against what the server last said.
 *
 * `null` rows mean nothing has loaded yet, which is emphatically not the same
 * as an empty list. An empty list is somebody having deleted every variable
 * and not saved it; nothing loaded is nothing staged.
 *
 * Reading the first as the second is what wedged this editor. It made the
 * page dirty before anybody had touched it, and the refresh that would have
 * filled it is suppressed while dirty — for the good reason that it must
 * never overwrite unsaved edits. So the section sat on a loading placeholder
 * that could not resolve, and the only way out was a reload, which landed in
 * the same state.
 */
export function changeSet(rows: EnvRow[] | null, server: EnvVar[]): ChangeSet {
  if (rows === null) {
    return { deletedKeys: [], changedRows: [], dirty: false }
  }
  const serverKeys = new Set(server.map((item) => item.key))
  const keptOriginals = new Set(
    rows.map((row) => row.originalKey).filter((key): key is string => key !== null),
  )
  const deletedKeys = [...serverKeys].filter((key) => !keptOriginals.has(key))
  const changedRows = rows.filter(
    (row) =>
      row.key !== '' &&
      (row.originalKey === null ||
        row.key !== row.originalKey ||
        Boolean(row.buildTime) !== Boolean(row.originalBuildTime) ||
        (row.known && row.value !== row.originalValue)),
  )
  return {
    deletedKeys,
    changedRows,
    dirty: deletedKeys.length > 0 || changedRows.length > 0,
  }
}
